import {
  U21Section,
  U21Alert,
  U21Button,
  U21Typography,
  U21Loading,
  U21LoadError,
  U21NoData,
  U21Spacer,
  U21Chip,
  U21HelpTooltip,
  U21CountLabel,
} from 'app/shared/u21-ui/components';
import { selectAlert } from 'app/modules/alerts/selectors';

import styled from 'styled-components';
import {
  IconArrowsLeftRight,
  IconListSearch,
  IconUser,
} from '@u21/tabler-icons';
import { ConditionBreakdown } from 'app/modules/detectionModels/components/ConditionBreakdown/ConditionBreakdown';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { formatDatetime } from 'app/shared/utils/date';
import { useFetchRule } from 'app/modules/rules/queries/useFetchRule';
import {
  isAdvancedModel,
  isIRSchema,
} from 'app/modules/detectionModels/helpers';
import { AlertStatus } from 'app/modules/rulesAdmin/models';
import { AlertUpdateTransaction } from 'app/modules/alerts/components/AlertUpdates/AlertUpdateTransaction';
import { AlertUpdateAction } from 'app/modules/alerts/components/AlertUpdates/AlertUpdateAction';
import { AlertUpdateEntity } from 'app/modules/alerts/components/AlertUpdates/AlertUpdateEntity';
import { AlertUpdateInstrument } from 'app/modules/alerts/components/AlertUpdates/AlertUpdateInstrument';
import { RuleStatus } from 'app/modules/rules/models';
import { useFetchAlertHitsInfinite } from 'app/modules/alerts/queries/useFetchAlertHitsInfinite';
import { RuleConditionTooltips } from 'app/modules/detectionModels/constants';

export const AlertUpdates = () => {
  const [selectedHitIndex, setSelectedHitIndex] = useState<number>(-1);
  const [open, setOpen] = useState<boolean>(false);
  const alert = useSelector(selectAlert);

  const {
    data: rule,
    isLoading: isRuleLoading,
    isError: isRuleError,
    refetch: refetchRule,
  } = useFetchRule(alert.rules[0].id, alert.rules[0].status as RuleStatus);

  const pageSize = 10;
  const alertId = alert?.id;

  const {
    allAlertHits,
    totalCount,
    query: { isError, isFetching, fetchNextPage, refetch },
  } = useFetchAlertHitsInfinite(alertId, pageSize);

  const ruleCondition =
    rule?.content.specification && isIRSchema(rule.content.specification)
      ? rule.content.specification.rule_condition
      : null;

  let content: React.ReactNode = (
    <U21Loading loading={isRuleLoading || isFetching} />
  );
  const filteredAlertHits = allAlertHits.filter(
    (hit, idx) =>
      idx === 0 ||
      hit.new_action_events_count > 0 ||
      hit.new_txn_events_count > 0,
  );

  if (isRuleError) {
    content = <U21LoadError label="rule error" onTryAgain={refetchRule} />;
  } else if (isError) {
    content = <U21LoadError label="alert updates" onTryAgain={refetch} />;
  } else if (allAlertHits && allAlertHits.length > 0) {
    content = filteredAlertHits.map((hit, idx) => (
      <U21Section
        key={hit.id}
        collapsed
        collapsible
        disableTitleCase
        title={
          <U21Spacer horizontal>
            <U21Typography variant="body1">
              {idx ===
              totalCount - 1 - (allAlertHits.length - filteredAlertHits.length)
                ? 'Flagged at'
                : 'Updated at'}
            </U21Typography>
            <U21Typography variant="subtitle1">
              {formatDatetime(hit.created_at)}
            </U21Typography>
            {hit.new_txn_events_count > 0 && (
              <U21CountLabel
                icon={<IconArrowsLeftRight size={16} />}
                variant="outlined"
                count={hit.new_txn_events_count}
                label="new event"
              />
            )}
            {hit.new_action_events_count > 0 && (
              <U21CountLabel
                icon={<IconArrowsLeftRight size={16} />}
                variant="outlined"
                count={hit.new_action_events_count}
                label="new action event"
              />
            )}
            {idx ===
              totalCount -
                1 -
                (allAlertHits.length - filteredAlertHits.length) &&
              hit.new_entities_count > 0 && (
                <U21CountLabel
                  variant="outlined"
                  icon={<IconUser size={16} />}
                  count={hit.new_entities_count}
                  label="new entity"
                />
              )}
            {idx ===
              totalCount -
                1 -
                (allAlertHits.length - filteredAlertHits.length) &&
              hit.new_instruments_count > 0 && (
                <U21CountLabel
                  variant="outlined"
                  icon={<IconUser size={16} />}
                  count={hit.new_instruments_count}
                  label="new instrument"
                />
              )}
          </U21Spacer>
        }
        action={
          <U21Button
            disabled={
              !(rule && isAdvancedModel(rule)) ||
              Object.keys(allAlertHits[idx].variable_snapshots).length === 0
            }
            aria-label={RuleConditionTooltips.VIEW_RULE_CONDITIONS}
            tooltip={
              Object.keys(allAlertHits[idx].variable_snapshots).length !== 0
                ? RuleConditionTooltips.VIEW_RULE_CONDITIONS
                : RuleConditionTooltips.RULE_CONDITIONS_NOT_AVAILABLE
            }
            onClick={(e) => {
              e.stopPropagation();
              setSelectedHitIndex(idx);
              setOpen(true);
            }}
          >
            <IconListSearch />
          </U21Button>
        }
      >
        <>
          {hit.new_txn_events_count > 0 && <AlertUpdateTransaction hit={hit} />}
          {hit.new_action_events_count > 0 && <AlertUpdateAction hit={hit} />}
          {idx === totalCount - 1 && hit.new_entities_count > 0 && (
            <AlertUpdateEntity hit={hit} />
          )}
          {idx === totalCount - 1 && hit.new_instruments_count > 0 && (
            <AlertUpdateInstrument hit={hit} />
          )}
        </>
      </U21Section>
    ));
  } else if (allAlertHits && allAlertHits.length === 0) {
    content = <U21NoData>No alert updates found.</U21NoData>;
  }

  return (
    <U21Section
      title={
        <U21Spacer horizontal useFlexGap>
          Alert Updates
          <FlexedU21HelpTooltip help="When a rule detects new activity that matches an existing open alert, the system updates the open alert with the latest information." />
        </U21Spacer>
      }
    >
      <U21Spacer>
        <U21Alert severity="info">
          <U21Typography variant="body2" component="div">
            {alert.status === AlertStatus.CLOSED ? (
              <>
                Alert has been closed. <U21Chip>{alert.rules[0].title}</U21Chip>{' '}
                will no longer update this alert.
              </>
            ) : (
              <>
                {alert.rules[0].execution_frequency && (
                  <>
                    <NegativeMarginU21Chip>
                      {alert.rules[0].title}
                    </NegativeMarginU21Chip>{' '}
                    executes every {alert.rules[0].readable_execution_frequency}
                    .{' '}
                  </>
                )}
                Updates will only be added if the rule detects new activity.
              </>
            )}
          </U21Typography>
        </U21Alert>
        <U21Spacer>{content}</U21Spacer>
        {allAlertHits.length < totalCount && (
          <U21Spacer align="center">
            <U21Button
              loading={isFetching}
              color="primary"
              onClick={() => fetchNextPage()}
              variant="text"
            >
              Load more
            </U21Button>
          </U21Spacer>
        )}
        <ConditionBreakdown
          open={open}
          close={() => setOpen(false)}
          ruleCondition={ruleCondition}
          ruleTitle={rule?.title}
          alertId={alert.id}
          alertHits={allAlertHits}
          alertHitIndex={selectedHitIndex}
          totalAlertHitIndex={filteredAlertHits.length}
          onHeaderAction={(idx: number) => {
            setSelectedHitIndex(idx);
          }}
        />
      </U21Spacer>
    </U21Section>
  );
};

const FlexedU21HelpTooltip = styled(U21HelpTooltip)`
  display: flex;
`;

const NegativeMarginU21Chip = styled(U21Chip)`
  margin-top: -4px;
`;
